import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth';

const Login = () => {
  const { session, signIn, loading } = useAuth();
  // Email starts empty. Browser-saved credentials autofill via autoComplete="email"
  // below; this avoids shipping a developer email default into Joel's prod deploy
  // at W7 handover (ADR-0008). Mechanically enforced by
  // scripts/check-no-dev-email-default.sh in pnpm validate.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (session) return <Navigate to="/inbox" replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error: err } = await signIn(email, password);
    setSubmitting(false);
    if (err) setError(err);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative px-4">
      <form onSubmit={onSubmit} className="neo-raised w-full max-w-sm rounded-[24px] p-8 space-y-5">
        <div className="flex flex-col items-center text-center">
          <span className="neo-chip w-12 h-12 flex items-center justify-center mb-3">
            <Activity className="w-6 h-6 text-forest" aria-hidden />
          </span>
          <h1 className="text-2xl font-serif font-semibold tracking-tight text-gradient-forest">
            Reverra
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Command Deck — owner sign-in</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>
        {error && <p className="text-sm text-alert">{error}</p>}
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </div>
  );
};

export default Login;
